import { useEffect, useRef } from 'react'
import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { usePickIntentStore } from '@/lib/store/usePickIntentStore'
import { useAddPickMutation } from '@/lib/api/mutations/usePickMutations'
import { toast } from 'sonner'
import { Loader2, CheckCircle2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { extractTargetUrl } from '@/lib/utils/share-parsing'
import { useNavigate, useLocation } from 'react-router-dom'

import { BackgroundTask } from '@capawesome/capacitor-background-task'

/**
 * Hook to listen for Android Share Intents (and general deep links)
 * that contain text/URLs to be added as new picks.
 * Re-unified to restore deduplication safety and bridge stability.
 */
export function useShareTarget() {
    const navigate = useNavigate()
    const location = useLocation()
    
    // Track the current path in a ref so the native bridge listeners (which only bind once)
    // always have access to the freshest location without causing re-renders/re-binds.
    const currentLocationRef = useRef(location.pathname)
    useEffect(() => {
        currentLocationRef.current = location.pathname
    }, [location.pathname])

    const { t } = useTranslation()
    const { clearSharedUrl, setSyncing, addPendingPick, removePendingPick } = usePickIntentStore()
    const { mutateAsync: addPick } = useAddPickMutation()
    // Use a Set to avoid double-processing (Fixes "Phantoms" bug)
    const processedUrlsRef = useRef<Set<string>>(new Set())
    // Track newly added pick IDs for the Aura animation on the Homepage
    const newlyAddedPickIdsRef = useRef<string[]>([])

    useEffect(() => {
        const handleUrl = async (url: string | null | undefined, source: string) => {
            const targetUrl = extractTargetUrl(url)
            if (!targetUrl) return

            // 🛡️ Deduplication: Restoration of the critical 5-second safety window
            if (processedUrlsRef.current.has(targetUrl)) return
            processedUrlsRef.current.add(targetUrl)
            setTimeout(() => processedUrlsRef.current.delete(targetUrl), 5000)

            addPendingPick(targetUrl)
            const currentCount = usePickIntentStore.getState().pendingPicks.length
            
            // 🎙️ STEP 1: Rolling Spinner
            toast(t('home.fetching_items', { count: currentCount }), {
                id: 'sync-toast',
                icon: <Loader2 className="h-5 w-5 animate-spin text-foreground" />,
                position: 'bottom-center',
                duration: Infinity
            })


            try {
                const pickData = await addPick({ url: targetUrl })
                if (pickData && pickData.id) newlyAddedPickIdsRef.current.push(pickData.id)
                console.log(`🚀 [${source}] Save successful:`, targetUrl)
            } catch (err) {
                console.error(`❌ [${source}] Save failed:`, err)
                toast.error('Failed to save link')
            } finally {
                // Update or dismiss
                removePendingPick(targetUrl)
                const remaining = usePickIntentStore.getState().pendingPicks.length
                if (remaining > 0) {
                    toast(t('home.fetching_items', { count: remaining }), { id: 'sync-toast' })
                } else {
                    // 🎙️ STEP 2: Success Morph
                    const isNotOnHomepage = currentLocationRef.current !== '/'
                    const highlightedPicks = [...newlyAddedPickIdsRef.current]
                    
                    toast(t('home.ready_in_my_picks'), {
                        id: 'sync-toast',
                        icon: <CheckCircle2 className="h-5 w-5 text-emerald-500" />,
                        duration: 4000,
                        action: isNotOnHomepage ? {
                            label: 'View',
                            onClick: () => {
                                navigate('/', { state: { highlightedPicks } })
                                newlyAddedPickIdsRef.current = [] // Reset
                            }
                        } : undefined
                    })
                    // If we didn't navigate, we can reset it now anyway
                    if (!isNotOnHomepage) newlyAddedPickIdsRef.current = []
                }
            }
        }

        // --- Bridge Listeners ---

        const urlListener = CapacitorApp.addListener('appUrlOpen', (data: any) => {
            if (data.url && (data.url.includes('/add') || data.url.includes('/share'))) {
                handleUrl(data.url, 'deep-link')
            }
            checkNativeBridge()
        })

        const stateListener = CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
            if (isActive) {
                checkNativeBridge()
            } else {
                // 🛡️ BACKGROUND SHIELD: Protect active network requests from OS suspension.
                // If the user shares a link and immediately swipes away, the OS will normally
                // sever all network connections. This wrapper begs iOS/Android for a grace
                // period, keeping the JS thread alive until the queue clears completely.
                const pendingCount = usePickIntentStore.getState().pendingPicks.length
                if (pendingCount > 0) {
                    const taskId = await BackgroundTask.beforeExit(async () => {
                        console.log("🛡️ Background Shield Activated for", pendingCount, "items.")
                        // Keep polling our store until the sync is complete
                        while (usePickIntentStore.getState().pendingPicks.length > 0) {
                            await new Promise(r => setTimeout(r, 500))
                        }
                        console.log("✅ Background Shield Deactivated. Safe to sleep.")
                        BackgroundTask.finish({ taskId })
                    })
                }
            }
        })

        ;(window as any).__SHUVO_SILENT_SAVE__ = (url: string) => {
            handleUrl(url, 'universal-bridge')
        }

        const checkNativeBridge = async () => {
            let queueData = ''

            if (Capacitor.getPlatform() === 'ios') {
                // 🚀 iOS PULL Architecture: Actively request the queue from Swift
                // This guarantees we only get the data when React is fully booted and ready.
                try {
                    ;(window as any).webkit?.messageHandlers?.shuvoCore?.postMessage("get-pending-saves")
                } catch (e) { /* ignore if not ready */ }
                
                // Give Swift 50ms to inject the string into window.__SHUVO_IOS_QUEUE__
                await new Promise(resolve => setTimeout(resolve, 50))
                
                const iosQueue = (window as any).__SHUVO_IOS_QUEUE__
                if (iosQueue) {
                    queueData = iosQueue
                    ;(window as any).__SHUVO_IOS_QUEUE__ = null
                    try {
                        ;(window as any).webkit.messageHandlers.shuvoCore.postMessage("clear-pending-saves")
                    } catch (e) { console.error("Bridge Error:", e) }
                }
            } else {
                const bridge = (window as any).ShuvoNative
                if (bridge?.getPendingQueue) {
                    queueData = bridge.getPendingQueue()
                }
            }

            if (!queueData) {
                usePickIntentStore.getState().setFirstLaunchProcessed()
                return
            }
            
            const urls = queueData.split('|||SHUVO_SEP|||')
            const { isFirstLaunch, setFirstLaunchProcessed } = usePickIntentStore.getState()
            
            if (isFirstLaunch) {
                setSyncing(true, urls.length)
                urls.forEach((url) => {
                    const target = extractTargetUrl(url)
                    if (target) addPendingPick(target)
                })
                setFirstLaunchProcessed()
                // Process these with isColdStart = true
                urls.forEach((url) => handleUrl(url, 'native-bridge-pull'))
            } else {
                urls.forEach((url) => handleUrl(url, 'native-bridge-pull'))
                if (Capacitor.getPlatform() !== 'ios') {
                    const bridge = (window as any).ShuvoNative
                    if (bridge?.clearPendingQueue) bridge.clearPendingQueue()
                }
            }
        }

        const runInitialChecks = async () => {
            // UI ANIMATION DELAY: We wait 500ms before pulling the queue on Cold Boot.
            // This guarantees the <Toaster /> component is fully painted in the DOM *before* 
            // the toast is fired, allowing the beautiful CSS slide-up animation to trigger.
            await new Promise(resolve => setTimeout(resolve, 500))

            await checkNativeBridge()
            const launchUrl = await CapacitorApp.getLaunchUrl()
            if (launchUrl?.url) {
                handleUrl(launchUrl.url, 'launch-url')
            }
            
            // Critical Safety Nudge: Some OS intents land just after mount.
            // We poll multiple times over the first 3 seconds to guarantee we don't miss it.
            ;[1000, 1500, 2000, 3000].forEach((delay: number) => {
                setTimeout(() => checkNativeBridge(), delay)
            })
        }

        runInitialChecks()

        return () => {
            urlListener.then(l => l.remove())
            stateListener.then(l => l.remove())
            delete (window as any).__SHUVO_SILENT_SAVE__
        }
    }, [clearSharedUrl, setSyncing]) // Removed addPick from dependencies to prevent infinite loops
}
